import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { webOrigin } from '../../utils/brand';
import { useNavigate } from 'react-router-dom';
import PortalLogo from '../PortalLogo';
import PortalRing from './PortalRing';
import StoryMedia from './StoryMedia';
import StoryVideoPlayer from '../media/StoryVideoPlayer';
import { tapZone } from '../media/storyVideoModel';
import PortalActions from './PortalActions';
import StoryInterstitial from './StoryInterstitial';
import IntroductionPortal from './IntroductionPortal';
import { REELS_CHANGED } from './reelRing';
import StorySheet from './StorySheet';
import ReportSheet from '../moderation/ReportSheet';
import StoryEndState from './StoryEndState';
import PortalCanvas from '../portal/canvas/PortalCanvas';
import PortalBackground from '../portal/canvas/PortalBackground';
import ImDownBeat from './ImDownBeat';
import { PortalActivityHandle, PortalActivitySheet, HostChrome } from './PortalActivitySheet';
import PortalShareSheet, { ShareBand, isAnimatedPortal } from './PortalShareSheet';
import { windowChip, normalizeLayout } from '../../utils/portalLayout';
import { windowBitsFromIso } from '../../utils/portalWindow';
import PortalDoorway from '../transitions/PortalDoorway';
import storyService from '../../services/storyService';
import signalService from '../../services/signalService';
import { useAuth } from '../../context/AuthContext';
import { usePortalPosting } from '../../context/PortalPostingContext';
import { useDialog } from '../../context/DialogContext';
import useMediaQuery from '../../utils/useMediaQuery';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';
import { ago, countdown } from '../../utils/surfaceFormat';
import { trackEvent } from '../../services/analytics';

/**
 * The full-screen story viewer. Design handoff §4.
 *
 * One reel per person: every Portal they have up plays in turn, oldest first, with a bar along
 * the top for each — and when the last one ends they either have stories behind it (the
 * interstitial offers them) or the viewer moves straight on to the next person.
 * No plate, no pause, nothing shown in between when there's nothing to show.
 *
 * Seen marks are batched: a fast swipe through six items is one request on the way out, not six
 * mid-gesture. An item counts as watched once it has been on screen for a second.
 */

const STILL_MS = 8000;        // how long a photo or a text Portal holds (story-activity handoff §1)
const SEEN_AFTER_MS = 1000;   // "watched" starts here (handoff: ≥1s)
const MUTE_KEY = 'pr.storyMuted';

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

/**
 * The venue the card draws. SHARE YOUR PORTAL captures the card as a picture meant for
 * Instagram and the like, so while it is open a place that isn't open to everyone draws the
 * locked placeholder — the host can see their own address, but it must never leave in the image.
 */
export function portalCardVenue(portal, sharing = false) {
  const city = (portal.city || '').split(',')[0];
  if (portal.venueName && !(sharing && !portal.venueOpen)) {
    return { name: portal.venueName, address: portal.venueAddress || portal.city || '', city };
  }
  if (portal.venueName || portal.venueLocked) return { locked: true, city };
  return null;
}

export default function StoryViewer({ reels = [], startUserId = null, doorRect = null, onClose, onChanged = null }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showConfirm } = useDialog();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const reduced = reducedMotion();

  const startIndex = Math.max(0, reels.findIndex((r) => r.authorUserId === startUserId));
  // Where a reel opens: the first thing in it you haven't watched, the way stories do — so a
  // second Portal doesn't make you sit through the one you saw an hour ago. All watched (or
  // your own) and it starts at the top.
  const firstUnseen = useCallback((r) => {
    const list = r?.portals?.length ? r.portals : (r?.portal ? [r.portal] : []);
    const seenFlags = [...(r?.introduction ? [!!r.introduction.isSeen] : []), ...list.map((p) => !!p.isSeen), ...(r?.stories || []).map((st) => !!st.isSeen)];
    const at = seenFlags.indexOf(false);
    return at < 0 ? 0 : at;
  }, []);
  const [personIndex, setPersonIndex] = useState(startIndex === -1 ? 0 : startIndex);
  const [itemIndex, setItemIndex] = useState(() => firstUnseen(reels[startIndex === -1 ? 0 : startIndex]));
  const [paused, setPaused] = useState(false);
  // A plain story clip plays in the story video player (handoff Part D): it owns the clock and
  // the skip ramp; this keeps the hold, the swipes and the two edge zones.
  const playerRef = useRef(null);
  const [coach, setCoach] = useState(false);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) !== 'false'; } catch { return true; }
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [seenCount, setSeenCount] = useState(null);
  const [interstitial, setInterstitial] = useState(false);
  const [endState, setEndState] = useState(null);   // 'failed' | 'encoding' | 'loggedOut' | 'end'
  const [progress, setProgress] = useState(0);
  // The doorway: the ring you tapped, opening into this. Until it has finished opening the
  // first progress segment holds — the story hasn't started yet, it's still arriving.
  const [doorOpen, setDoorOpen] = useState(!doorRect);
  const [doorClosing, setDoorClosing] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  // The I'M DOWN beat: { phase: 'beat'|'out'|'error', title, result } while it plays.
  const [beat, setBeat] = useState(null);
  const beatTimers = useRef([]);
  const beatResult = useRef(null);
  // Typing a reply pauses the story; so does the host's activity sheet. Both show PAUSED.
  const [replyFocused, setReplyFocused] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activity, setActivity] = useState(null);
  // Asks sent from this viewer, by Portal — the button reads REQUESTED until the host answers.
  const [requested, setRequested] = useState({});
  // SHARE YOUR PORTAL: the card scales down over the sheet; `capturing` stills the logo and
  // hides the sticker hint for the render. The copied link is remembered for the session.
  const [shareOpen, setShareOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const stageRef = useRef(null);

  const reel = reels[personIndex] || null;
  const nextReel = reels[personIndex + 1] || null;
  const prevReel = reels[personIndex - 1] || null;

  // The reel as one sequence: the Portal is item 0, the stories follow. The gate between them
  // is a rule about how it ENDS, not a second list.
  const items = useMemo(() => {
    if (!reel) return [];
    const list = [];
    // An introduction comes first, and it is a page to read rather than a clip with a clock.
    if (reel.introduction) list.push({ type: 'intro', intro: reel.introduction });
    // `portals` is all of them, oldest first. `portal` alone is the older shape — a reel that
    // only ever carried the newest — and still arrives from anything that hands us one reel.
    const portals = reel.portals?.length ? reel.portals : (reel.portal ? [reel.portal] : []);
    portals.forEach((p) => list.push({ type: 'portal', portal: p, media: p.media }));
    (reel.stories || []).forEach((s) => list.push({ type: 'story', media: s }));
    return list;
  }, [reel]);

  const firstStoryIndex = Math.max(0, items.findIndex((it) => it.type === 'story'));
  const item = items[itemIndex] || null;
  const isIntro = item?.type === 'intro';
  // THE Portal — the one on screen. With several in a reel, everything the chrome offers (edit,
  // remove, report, the link, the countdown) has to be about this one and not the newest, which
  // is all `reel.portal` can tell you. Falls back to it while a story is up.
  const shown = (item?.type === 'portal' ? item.portal : null) || reel?.portal || null;
  // Your own Portal whose clip is still going up or encoding: the server has only the words so
  // far, but the phone has the picture — play that, framed and trimmed as you sent it, instead
  // of a words-only card that makes it look like the clip was lost.
  const { posting } = usePortalPosting();
  // Memoised on what matters: posting updates on every upload percent, and a new media object
  // each time would restart the viewer's clock.
  const pEvent = posting?.eventId; const pUrl = posting?.localUrl; const pKind = posting?.kind;
  const pTrim = posting?.trim; const pFrame = posting?.frame;
  const localMedia = useMemo(() => (
    item?.type === 'portal' && !item.media && reel?.isOwn && pEvent === item.portal.id && pUrl
      && (pKind === 'video' || pKind === 'photo')
      ? {
        id: `local-${pEvent}`,
        kind: pKind,
        mediaUrl: pUrl,
        mediaStatus: 'ready',
        trimStartMs: pTrim?.startMs ?? null,
        trimEndMs: pTrim?.endMs ?? null,
        frame: pFrame || null,
      }
      : null
  ), [item, reel?.isOwn, pEvent, pUrl, pKind, pTrim, pFrame]);
  const media = item?.media || localMedia;
  const isOwn = !!reel?.isOwn;

  const seenMarks = useRef(new Map());
  const timerRef = useRef(null);
  const startedAt = useRef(0);
  const elapsedBefore = useRef(0);
  const durationRef = useRef(STILL_MS);

  const flushSeen = useCallback(async () => {
    if (seenMarks.current.size === 0 || !isAuthenticated) return;
    const marks = Array.from(seenMarks.current.values());
    seenMarks.current.clear();
    await storyService.markViewed(marks);
    onChanged?.();
  }, [isAuthenticated, onChanged]);

  const close = useCallback(() => {
    flushSeen();
    trackEvent('story_exit');
    // Back through the ring it came out of; the rail marks it seen when it lands.
    if (doorRect && !doorClosing) { setDoorClosing(true); return; }
    onClose?.();
  }, [flushSeen, onClose, doorRect, doorClosing]);

  // ---- advancing ----

  const goPerson = useCallback((delta) => {
    setInterstitial(false);
    setEndState(null);
    setPersonIndex((i) => {
      const next = i + delta;
      if (next < 0) return i;
      if (next >= reels.length) { setEndState('end'); return i; }
      setItemIndex(firstUnseen(reels[next]));
      return next;
    });
  }, [reels, firstUnseen]);

  const advance = useCallback((natural) => {
    if (!reel) return;
    // The LAST Portal: between two of someone's Portals we just carry on, the way stories do.
    const isPortalItem = items[itemIndex]?.type === 'portal' && items[itemIndex + 1]?.type !== 'portal';
    const hasStories = (reel.stories || []).length > 0;
    // The one place the viewer stops on its own: the Portals ended and there's more behind them.
    if (natural && isPortalItem && hasStories) {
      setInterstitial(true);
      return;
    }
    if (itemIndex + 1 < items.length) {
      setItemIndex((i) => i + 1);
      trackEvent('story_advance');
      return;
    }
    if (personIndex + 1 >= reels.length) { setEndState('end'); return; }
    goPerson(1);
  }, [reel, items, itemIndex, personIndex, reels.length, goPerson]);

  const back = useCallback(() => {
    if (interstitial) { setInterstitial(false); return; }
    if (itemIndex > 0) { setItemIndex((i) => i - 1); return; }
    if (personIndex > 0) goPerson(-1);
  }, [interstitial, itemIndex, personIndex, goPerson]);

  // ---- the clock ----

  useEffect(() => {
    setProgress(0);
    elapsedBefore.current = 0;
    setEndState((prev) => (prev === 'end' ? prev : null));
    if (!media) { durationRef.current = STILL_MS; return undefined; }
    if (media.mediaStatus === 'encoding') { setEndState('encoding'); return undefined; }
    if (media.mediaStatus === 'failed') { setEndState('failed'); return undefined; }
    durationRef.current = media.kind === 'video' && media.durationSec
      ? Math.max(1000, media.durationSec * 1000)
      : STILL_MS;
    return undefined;
  }, [media, itemIndex, personIndex]);

  const held = isIntro || paused || sheetOpen || interstitial || !!endState || !!beat || replyFocused || activityOpen || shareOpen;
  const playerVideo = item?.type !== 'portal' && media?.kind === 'video' && !!media?.mediaUrl && !endState;
  // The share sheet holds the CLOCK, not the card. `data-paused` stops every CSS animation
  // inside the card — the moving backgrounds, the portal in the lockup — so pausing it while
  // the sheet is up froze the very thing being previewed, and the loop recorded ten seconds of
  // a still. Everything else that holds the story (a hold, a sheet, the end plate) still does.
  const cardHeld = held && !shareOpen;
  useEffect(() => {
    if (!item || held || !doorOpen) return undefined;
    startedAt.current = Date.now();
    let raf = 0;
    const tick = () => {
      const elapsed = elapsedBefore.current + (Date.now() - startedAt.current);
      const pct = Math.min(1, elapsed / durationRef.current);
      // A clip in the player draws its own progress (seeks move it); the wall clock only
      // counts for the seen mark, and the player's onEnded is what advances.
      if (!playerVideo) setProgress(pct);

      if (elapsed >= SEEN_AFTER_MS && item) {
        const key = item.type === 'portal' ? `portal:${item.portal.id}` : `story:${item.media.id}`;
        if (!seenMarks.current.has(key)) {
          seenMarks.current.set(key, {
            itemType: item.type === 'portal' ? 'portal' : 'story',
            itemId: item.type === 'portal' ? item.portal.id : item.media.id,
            completed: false,
          });
        }
      }

      if (pct >= 1 && !playerVideo) {
        const key = item.type === 'portal' ? `portal:${item.portal.id}` : `story:${item.media.id}`;
        const mark = seenMarks.current.get(key);
        if (mark) mark.completed = true;
        advance(true);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      elapsedBefore.current += Date.now() - startedAt.current;
    };
  }, [item, held, advance, doorOpen, playerVideo]);
  // An introduction has no clock, so the second on screen that makes it "seen" is its own timer.
  const [reporting, setReporting] = useState(null);
  const introId = isIntro ? item.intro.id : null;
  useEffect(() => {
    if (!introId || !doorOpen) return undefined;
    const t = setTimeout(() => {
      seenMarks.current.set(`story:${introId}`, { itemType: 'story', itemId: introId, completed: true });
    }, SEEN_AFTER_MS);
    return () => clearTimeout(t);
  }, [introId, doorOpen]);
  // Adding someone keeps their introduction open until this closes, THEN it leaves the rail.
  const railDirty = useRef(false);
  useEffect(() => () => {
    if (railDirty.current) window.dispatchEvent(new Event(REELS_CHANGED));
  }, []);
  const playerEnded = useCallback(() => {
    if (!item) return;
    const key = item.type === 'portal' ? `portal:${item.portal.id}` : `story:${item.media.id}`;
    const mark = seenMarks.current.get(key);
    if (mark) mark.completed = true;
    if (!shareOpen) advance(true);
  }, [item, shareOpen, advance]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // The host's handle needs the faces and counts; opening the sheet asks again for fresh ones.
  const ownPortalId = isOwn && item?.type === 'portal' ? item.portal.id : null;
  useEffect(() => {
    if (!ownPortalId || !isAuthenticated) { setActivity(null); return undefined; }
    let done = false;
    storyService.getActivity(ownPortalId).then((r) => { if (!done && r?.success) setActivity(r.data || { down: [], views: [] }); });
    return () => { done = true; };
  }, [ownPortalId, activityOpen, isAuthenticated]);
  useEffect(() => { setActivityOpen(false); setReplyFocused(false); setShareOpen(false); }, [personIndex, itemIndex]);
  useEffect(() => { trackEvent('story_open'); return () => { flushSeen(); }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- keyboard ----

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowRight') { goPerson(1); return; }
      if (e.key === 'ArrowLeft') { goPerson(-1); return; }
      if (e.key === 'm' || e.key === 'M') { toggleMute(); return; }
      if (e.key === ' ') { e.preventDefault(); setPaused(true); return; }
      if (e.key === 'Enter') { primaryAction(); }
    };
    const onKeyUp = (e) => { if (e.key === ' ') setPaused(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, goPerson, interstitial, item, reel]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, String(next)); } catch { /* private mode */ }
      return next;
    });
  };

  // ---- actions ----

  const clearBeat = () => { beatTimers.current.forEach(clearTimeout); beatTimers.current = []; };
  useEffect(() => clearBeat, []);

  /**
   * I'M DOWN (handoff v3 §7): the join request goes out at t=0 and the beat plays over the
   * card — JOINING… at 300ms, YOU'RE IN at 1100ms, out at 1500ms — then the Portal's own page
   * slides up in the viewer's place, in its "you're in" state, with a way back to the story.
   * Reduced motion holds 600ms and cuts. A refused join finishes the beat, then offers RETRY.
   */
  /**
   * ASK TO JOIN on a curated Portal (curated-portal handoff §2): the same beat, ASKING… then
   * REQUEST SENT at 1100ms, then it fades back to the story at 1700ms — no page. The button
   * reads REQUESTED until the host lets them in.
   */
  const runRequestBeat = (portal) => {
    clearBeat();
    beatResult.current = null;
    setBusy(true);
    const host = (reel?.handle || reel?.displayName || '').toUpperCase();
    setBeat({ phase: 'beat', title: 'ASKING…', sub: `${host} PICKS WHO’S IN · YOU’LL GET A PING` });
    trackEvent('portal_cta_click', { portalId: portal.id, curated: true });
    signalService.imDown(portal.id).then((r) => { beatResult.current = r?.success ? 'ok' : 'fail'; }).catch(() => { beatResult.current = 'fail'; });
    const finish = () => {
      setBusy(false);
      if (beatResult.current === 'fail') { setBeat({ phase: 'error' }); return; }
      setRequested((m) => ({ ...m, [portal.id]: true }));
      onChanged?.();
      setBeat(null);
    };
    const T = (ms, fn) => beatTimers.current.push(setTimeout(fn, ms));
    if (reduced) { T(600, finish); return; }
    T(1100, () => setBeat((b) => (b && b.phase === 'beat' ? { ...b, title: 'REQUEST SENT' } : b)));
    T(1700, () => {
      const go = () => { setBeat((b) => (b ? { ...b, phase: 'out' } : b)); T(300, finish); };
      if (beatResult.current) go();
      else { const poll = setInterval(() => { if (beatResult.current) { clearInterval(poll); go(); } }, 100); beatTimers.current.push(poll); }
    });
  };

  const runBeat = (portal) => {
    clearBeat();
    beatResult.current = null;
    setBusy(true);
    setBeat({ phase: 'beat', title: 'JOINING…' });
    trackEvent('portal_cta_click', { portalId: portal.id });
    signalService.imDown(portal.id).then((r) => { beatResult.current = r?.success ? 'ok' : 'fail'; }).catch(() => { beatResult.current = 'fail'; });
    // The page takes the viewer's place in history, so Back returns to where you were browsing;
    // BACK TO THE STORY on the page reopens this reel by its address.
    const backTo = `${window.location.pathname}${window.location.search}`;
    const finish = () => {
      setBusy(false);
      if (beatResult.current === 'fail') { setBeat({ phase: 'error' }); return; }
      flushSeen();
      onChanged?.();
      navigate(`/portals/${portal.id}?joined=1`, { replace: true, state: { fromStory: backTo } });
    };
    const T = (ms, fn) => beatTimers.current.push(setTimeout(fn, ms));
    if (reduced) { T(600, finish); return; }
    T(1100, () => setBeat((b) => (b && b.phase === 'beat' ? { ...b, title: 'YOU’RE IN' } : b)));
    T(1500, () => {
      // Still waiting on the server: hold on YOU'RE IN until it answers, then go.
      const go = () => { setBeat((b) => (b ? { ...b, phase: 'out' } : b)); finish(); };
      if (beatResult.current) go();
      else { const poll = setInterval(() => { if (beatResult.current) { clearInterval(poll); go(); } }, 100); beatTimers.current.push(poll); }
    });
  };

  const primaryAction = async () => {
    if (interstitial) { setInterstitial(false); setItemIndex(firstStoryIndex); return; }
    if (isIntro) return;
    const portal = shown;
    if (!portal) return;
    if (!isAuthenticated) { navigate(`/login?return=/portals/${portal.id}`); return; }
    if (requested[portal.id] || portal.viewerState === 'applied_pending') return;   // waiting on the host
    if (isOwn || portal.viewerState !== 'open' || beat) { navigate(`/portals/${portal.id}`); return; }
    if (portal.mode === 'curated') runRequestBeat(portal); else runBeat(portal);
  };

  // LET IN / pass on the host's sheet: the row moves at once, and comes back if the server says no.
  const decide = async (userId, action) => {
    const portalId = shown?.id;
    if (!portalId || !activity) return;
    const before = activity;
    setActivity({
      down: action === 'accept'
        ? activity.down.map((r) => (r.userId === userId ? { ...r, status: 'in' } : r))
        : activity.down.filter((r) => r.userId !== userId),
      views: activity.views.map((r) => (r.userId === userId ? { ...r, isDown: action === 'accept', status: action === 'accept' ? 'in' : null } : r)),
    });
    const r = await signalService.reviewApplicant(portalId, userId, action);
    if (!r?.success) setActivity(before);
    else onChanged?.();
  };

  const openSheet = async () => {
    setSheetOpen(true);
    if (isOwn && shown) {
      const r = await storyService.getSeenCount(shown.id, 'portal');
      if (r.success) setSeenCount(r.data?.count ?? 0);
    }
  };

  const sheetRows = isOwn
    ? [
      { key: 'edit', label: 'Edit the details', onClick: () => navigate(`/portals/${shown?.id}/edit`) },
      { key: 'add', label: 'Add a story to it', onClick: () => navigate(`/portals/new?story=1&portal=${shown?.id}`) },
      { key: 'copy', label: 'Copy link', onClick: () => copyLink() },
      {
        key: 'delete',
        label: 'Delete this Portal',
        destructive: true,
        onClick: async () => {
          setSheetOpen(false);
          const ok = await showConfirm({
            title: 'DELETE THIS PORTAL?',
            message: 'It disappears for everyone, along with anything posted with it.',
            confirmLabel: 'DELETE',
            cancelLabel: 'KEEP IT',
            variant: 'danger',
          });
          if (!ok) return;
          await signalService.remove(shown.id);
          onChanged?.();
          close();
        },
      },
      { key: 'cancel', label: 'Cancel', onClick: () => setSheetOpen(false) },
    ]
    : [
      {
        key: 'mute',
        label: `Mute ${(reel?.handle || reel?.displayName || '').toLowerCase()}`,
        onClick: async () => { await storyService.mute(reel.authorUserId); setSheetOpen(false); onChanged?.(); goPerson(1); },
      },
      {
        key: 'hide',
        label: 'Hide this Portal',
        onClick: async () => { if (shown) await signalService.dismiss(shown.id); setSheetOpen(false); onChanged?.(); goPerson(1); },
      },
      { key: 'copy', label: 'Copy link', onClick: () => copyLink() },
      {
        key: 'report',
        label: 'Report this Portal',
        destructive: true,
        onClick: () => {
          setSheetOpen(false);
          setReporting(item?.type === 'story'
            ? { what: 'this story', storyId: item.media?.id || null }
            : { what: 'this Portal', eventId: shown?.id || null });
        },
      },
      { key: 'cancel', label: 'Cancel', onClick: () => setSheetOpen(false) },
    ];

  const copyLink = () => {
    const url = `${webOrigin()}/portals/${shown?.id || ''}`;
    try { navigator.clipboard?.writeText(url); } catch { /* clipboard refused */ }
    setSheetOpen(false);
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !reel) return;
    setReply('');
    // A reply to a story is just a DM, with a line saying what it's about — the chat is 1:1
    // and already does everything a reply needs.
    const { default: messageService } = await import('../../services/messageService');
    const portalId = shown?.id || null;
    const thread = await messageService.createOrGetThread(reel.authorUserId, null, null, portalId);
    const threadId = thread?.data?.id || thread?.data?.threadId;
    // The Portal rides along as portalEventId, so the thread draws the reply card above the
    // bubble instead of a "Re:" prefix in the words.
    if (threadId) await messageService.sendMessage(threadId, portalId ? { body: text, portalEventId: portalId } : { body: `Re: your Portal — ${text}` });
    trackEvent('story_reply');
  };

  // ---- gestures ----

  const touch = useRef(null);
  // Swipe the picture down to close — it follows the finger and shrinks on the way out.
  // The hook's listeners are on the frame itself, so they run before the handlers below and
  // `swipe.claimed` tells them a dismiss already owns this gesture.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => close(), enabled: !isDesktop && !isIntro });
  // A tap on a control (pause, mute, ⋯, the slab's buttons, the reply box) is that control's —
  // never a tap on the frame that also skips ahead or holds the clock.
  const onControl = (e) => !!e.target?.closest?.('button, a, input, textarea, select, [data-no-tap]');
  const onTouchStart = (e) => {
    if (onControl(e)) { touch.current = null; return; }
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, at: Date.now() };
    timerRef.current = setTimeout(() => setPaused(true), 220);
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    clearTimeout(timerRef.current);
    const wasPaused = paused;
    setPaused(false);
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // An introduction scrolls: taps are its own and a sideways swipe is the only page turn.
    if (isIntro) {
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx > 0) back();
        else if (itemIndex + 1 < items.length || personIndex + 1 < reels.length) advance(false);
        else close();
      }
      return;
    }
    // A downward drag was already handled as a dismiss (closed, or sprung back) — it is
    // never also a tap or a page turn.
    if (swipe.claimed.current) return;
    // The host's own card: a swipe up opens who's down and who viewed.
    if (isOwn && item?.type === 'portal' && dy < -40 && Math.abs(dy) > Math.abs(dx)) { setActivityOpen(true); return; }
    if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) > 60) { goPerson(dx < 0 ? 1 : -1); return; }
    if (wasPaused || Date.now() - start.at > 220) return;   // that was a hold, not a tap
    if (coach) return;                                       // the tips overlay takes the tap
    const zone = start.x / (e.currentTarget.clientWidth || window.innerWidth);
    if (playerVideo) {
      // The player's four zones (§4b): edges change item, the middle halves skip on the ramp.
      const z = tapZone(zone, true);
      if (z === 'prev') back();
      else if (z === 'next') advance(false);
      else playerRef.current?.skip(z === 'rewind' ? -1 : 1);
      return;
    }
    if (zone < 0.3) back(); else advance(false);
  };

  if (!reel) return null;

  const progressSegments = items.length || 1;
  const shareSheet = shareOpen && item?.type === 'portal' ? (
    <PortalShareSheet
      stageRef={stageRef}
      portal={item.portal}
      media={media}
      animated={isAnimatedPortal(item.portal, media)}
      onCapturing={setCapturing}
      copiedAtStart={linkCopied}
      onCopied={() => setLinkCopied(true)}
      onClose={() => setShareOpen(false)}
      reduced={reduced}
    />
  ) : null;
  const cardBody = isIntro ? (
    <IntroductionPortal
      reel={reel}
      intro={item.intro}
      onClose={close}
      onRailChanged={() => { railDirty.current = true; }}
      onSkip={() => { if (personIndex + 1 < reels.length) goPerson(1); else close(); }}
    />
  ) : (
    <>
      {/* A Portal: its picture (or background) with the words and the venue on it, at the
          layout the sender chose. A story: the clip alone. */}
      {item?.type === 'portal' ? (
        <PortalCanvas
          media={media}
          text={item.portal.text}
          chip={windowChip(item.portal.startUtc, item.portal.expiresAtUtc)}
          layout={item.portal.layout}
          venue={portalCardVenue(item.portal, shareOpen)}
          venueDate={windowBitsFromIso(item.portal.startUtc)}
          event={item.portal.sourceEvent || null}
          eventLinkTo={item.portal.sourceEvent?.inviteCode ? `/events/${item.portal.sourceEvent.inviteCode}` : null}
          active={doorOpen && !cardHeld}
          muted={muted}
          paused={cardHeld}
          // SHARE YOUR PORTAL holds the story but not the clip: it keeps playing (and loops) so
          // the capture has something to record, and reaching its end must never move on to
          // the next story — that was pulling the card out from under a share in progress.
          loop={shareOpen}
          scrim={false}
          onError={() => setEndState('failed')}
          onDuration={(d) => {
            // A trimmed clip is only its window: the file's own length would run the clock on
            // long after the player has looped back to the start of the slice.
            const clip = media && media.trimEndMs > media.trimStartMs ? (media.trimEndMs - media.trimStartMs) / 1000 : d;
            if (clip) durationRef.current = Math.max(1000, clip * 1000);
          }}
          onEnded={() => { if (!shareOpen) advance(true); }}
          className="absolute inset-0"
        />
      ) : playerVideo ? (
        <StoryVideoPlayer
          ref={playerRef}
          key={media.id || media.mediaUrl}
          item={media}
          active={doorOpen && !cardHeld}
          muted={muted}
          paused={cardHeld}
          tapZones={false}
          tiers={[3, 5]}
          bottomInset={item?.media?.caption ? 96 : isDesktop ? 12 : 40}
          onError={() => setEndState('failed')}
          onDuration={(d) => { if (d) durationRef.current = Math.max(1000, d * 1000); }}
          onTime={(t, d) => { if (d > 0) setProgress(Math.min(1, t / d)); }}
          onEnded={playerEnded}
          onCoachChange={setCoach}
        />
      ) : media ? (
        <StoryMedia
          item={media}
          active={doorOpen && !cardHeld}
          muted={muted}
          paused={cardHeld}
          onError={() => setEndState('failed')}
          onDuration={(d) => { if (d) durationRef.current = Math.max(1000, d * 1000); }}
          onEnded={() => { if (!shareOpen) advance(true); }}
        />
      ) : null}

      {/* Top scrim so the progress and the header never sit on bare video. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10"
        style={{ height: 155, background: 'linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0))' }}
        aria-hidden
      />

      <div className="absolute inset-x-0 z-20" style={{ top: isDesktop ? 10 : 40, padding: '0 8px', visibility: coach ? 'hidden' : 'visible' }}>
        <div className="flex" style={{ gap: 4, opacity: shareOpen ? 0 : 1 }} aria-hidden data-share-skip>
          {Array.from({ length: progressSegments }).map((_, i) => (
            <div key={i} className="flex-1" style={{ height: 2, background: 'rgba(255,255,255,0.35)' }}>
              <div
                style={{
                  height: '100%',
                  width: i < itemIndex ? '100%' : i === itemIndex ? `${Math.round(progress * 100)}%` : '0%',
                  background: '#FFFFFF',
                  transition: reduced ? 'none' : 'width 80ms linear',
                }}
              />
            </div>
          ))}
        </div>

        <div className="mt-3.5 flex items-start justify-between" style={{ padding: '0 8px' }}>
          {/* The face and the name open the person's Portal Page (`/{handle}`). A button, so the
              frame's tap handler (which skips the controls) never reads it as "next". */}
          <button
            type="button"
            data-testid="story-author"
            aria-label={`Open ${reel.handle || reel.displayName || 'their'}’s Portal Page`}
            onClick={(e) => { e.stopPropagation(); navigate(reel.handle ? `/${reel.handle}` : `/users/${reel.authorUserId}`); }}
            className="flex items-start text-left"
            style={{ gap: 10, background: 'none', border: 0, padding: 0, cursor: 'pointer', color: '#fff' }}
          >
            <span className="block flex-shrink-0 overflow-hidden border-2 border-white" style={{ width: 34, height: 34 }}>
              {reel.avatarUrl
                ? <img src={reel.avatarUrl} alt="" className="h-full w-full object-cover" />
                : <span className="flex h-full w-full items-center justify-center bg-black font-mono text-xs font-bold text-white">{(reel.displayName || '?')[0]}</span>}
            </span>
            <span>
              <span className="block text-white">
                <span className="font-mono font-bold" style={{ fontSize: 13 }}>{reel.handle || reel.displayName}</span>
                <span className="ml-2 font-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,0.70)' }}>
                  {media?.createdAt ? ago(media.createdAt) : ''}
                </span>
              </span>
              {shown?.expiresAtUtc && (
                <span className="mt-0.5 block font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.10em', color: '#FF00AA' }}>
                  Portal · {countdown(shown.expiresAtUtc, { style: 'short' }).text} left
                </span>
              )}
            </span>
          </button>

          <div className="flex items-center text-white" style={{ gap: 14, opacity: shareOpen ? 0 : 1 }} data-share-skip>
            {(replyFocused || activityOpen) && !shareOpen && (
              <span data-testid="paused-chip" className="flex items-center font-mono font-bold uppercase" style={{ gap: 6, height: 26, padding: '0 12px', background: 'rgba(0,0,0,.72)', borderRadius: 999, fontSize: 10, letterSpacing: '0.12em' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden><rect x="5" y="4" width="5" height="16" /><rect x="14" y="4" width="5" height="16" /></svg>
                Paused
              </span>
            )}
            <button type="button" aria-label={paused ? 'Play' : 'Pause'} onClick={() => setPaused((p) => !p)}>
              {paused ? <PlayGlyph /> : <PauseGlyph />}
            </button>
            <button type="button" aria-label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute}>
              {muted ? <MutedGlyph /> : <SoundGlyph />}
            </button>
            <button type="button" aria-label="More" onClick={openSheet}><DotsGlyph /></button>
            {!isDesktop && <button type="button" aria-label="Close" onClick={close}><CloseGlyph /></button>}
          </div>
        </div>
      </div>

      {/* The Portal's actions, in the area the composer keeps clear. Stories carry only their caption. */}
      {item?.type === 'portal' && !endState && isOwn && shareOpen && <ShareBand hint={!capturing} still={capturing} />}
      {item?.type === 'portal' && !endState && isOwn && !shareOpen && !activityOpen && (
        <HostChrome onShare={() => { setActivityOpen(false); setShareOpen(true); }} onManage={() => navigate(`/portals/${item.portal.id}`)} />
      )}
      {item?.type === 'portal' && !endState && isOwn && !shareOpen && (
        <>
          <PortalActivityHandle
            down={activity?.down || []}
            downCount={activity ? activity.down.length : (item.portal.acceptedCount ?? 0)}
            waitingCount={activity ? activity.down.filter((r) => r.status === 'pending').length : 0}
            viewCount={activity ? activity.views.length : 0}
            onOpen={() => setActivityOpen(true)}
            reduced={reduced}
          />
          {activityOpen && (
            <PortalActivitySheet
              activity={activity}
              mode={item.portal.mode}
              onLetIn={(userId) => decide(userId, 'accept')}
              onPass={(userId) => decide(userId, 'decline')}
              onClose={() => setActivityOpen(false)}
              onShare={() => { setActivityOpen(false); setShareOpen(true); }}
              onManage={() => navigate(`/portals/${item.portal.id}`)}
              reduced={reduced}
            />
          )}
        </>
      )}
      {item?.type === 'portal' && !endState && !isOwn && (
        <PortalActions
          portal={item.portal}
          isOwn={isOwn}
          busy={busy}
          joined={!!beat && beat.phase !== 'error' && item.portal.mode !== 'curated'}
          requested={!!requested[item.portal.id] || (!!beat && beat.phase !== 'error' && item.portal.mode === 'curated')}
          onAction={primaryAction}
          onViewPortal={() => navigate(`/portals/${item.portal.id}`)}
          replyValue={reply}
          onReplyChange={setReply}
          onReplyFocus={setReplyFocused}
          onReplySend={!isOwn ? sendReply : null}
          replyTo={(reel.handle || reel.displayName || '').toLowerCase()}
        />
      )}
      {beat && item?.type === 'portal' && (
        <ImDownBeat
          phase={beat.phase}
          title={beat.title}
          sub={beat.sub || null}
          avatarUrl={reel.avatarUrl}
          handle={reel.handle || reel.displayName || ''}
          inCount={(item.portal.acceptedCount ?? 0) + 1}
          winShort={shown?.expiresAtUtc ? `${countdown(shown.expiresAtUtc, { style: 'short' }).text} LEFT` : ''}
          background={media ? (
            <img src={media.thumbnailUrl || media.mediaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : <PortalBackground kind={normalizeLayout(item.portal.layout).background} />}
          onRetry={() => runBeat(item.portal)}
          onClose={() => { clearBeat(); setBeat(null); }}
        />
      )}
      {item?.type === 'story' && item.media?.caption && !endState && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20">
          <div style={{ height: 110, background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.85))' }} aria-hidden />
          {/* The media stays full-bleed; only this bar has to clear the
              navigation bar, so it pads rather than lifting off the edge. */}
          <div style={{ background: '#000', borderTop: '2px solid #FFF', padding: '16px 16px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
            <p className="text-white" style={{ fontSize: 17, lineHeight: 1.3 }}>{item.media.caption}</p>
          </div>
          {!isDesktop && <div style={{ height: 34, background: '#000' }} aria-hidden />}
        </div>
      )}

      {interstitial && (
        <StoryInterstitial
          reel={reel}
          nextHandle={nextReel?.handle || nextReel?.displayName || null}
          reduced={reduced}
          onViewStories={() => { trackEvent('story_interstitial_view_all'); setInterstitial(false); setItemIndex(firstStoryIndex); }}
          onNext={() => { trackEvent('story_interstitial_next'); goPerson(1); }}
          onBackToFeatured={close}
        />
      )}

      {endState && (
        <StoryEndState
          kind={endState}
          name={reel.handle || reel.displayName || 'They'}
          count={reels.length}
          onPrimary={() => {
            if (endState === 'failed') { setEndState(null); setProgress(0); return; }
            if (endState === 'encoding') { navigate(`/portals/${shown?.id}`); return; }
            if (endState === 'loggedOut') { navigate('/login?return=/featured'); return; }
            close();
          }}
          onSecondary={() => {
            if (endState === 'end') { navigate('/portals/new'); return; }
            if (endState === 'loggedOut') { close(); return; }
            goPerson(1);
          }}
        />
      )}

      {reporting && (
        <ReportSheet
          userId={reel?.authorUserId}
          displayName={reel?.displayName}
          what={reporting.what}
          eventId={reporting.eventId}
          storyId={reporting.storyId}
          onClose={() => setReporting(null)}
          onBlocked={() => { onChanged?.(); close(); }}
        />
      )}
      <StorySheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        rows={sheetRows}
        seenCount={isOwn ? seenCount : null}
        reduced={reduced}
      />
    </>
  );
  // The card on its stage: while sharing it scales to half from the top centre, over the sheet.
  const staged = (
    <>
      <div
        ref={stageRef}
        data-testid="story-stage"
        className="absolute inset-0"
        style={{ transformOrigin: '50% 0', transform: shareOpen ? 'translateY(58px) scale(.5)' : 'none', borderRadius: shareOpen ? 28 : 0, overflow: 'hidden', background: '#000', transition: reduced ? 'none' : 'transform .44s cubic-bezier(.2,.7,.2,1), border-radius .44s' }}
      >
        {cardBody}
      </div>
      {shareSheet}
    </>
  );

  // ---- frame ----

  // Straight through when there is no ring to come out of (a deep link, say).
  const throughDoor = (frame) => (
    <PortalDoorway
      ringRect={doorRect}
      closing={doorClosing}
      onOpened={() => setDoorOpen(true)}
      onClosed={() => onClose?.()}
    >
      {frame}
    </PortalDoorway>
  );

  if (!isDesktop) {
    return throughDoor(
      <div
        ref={frameRef}
        className="fixed inset-0 z-[100]"
        style={{ background: '#000', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none', ...swipe.style }}
        role="dialog"
        aria-modal="true"
        aria-label={`Portal from ${reel.handle || reel.displayName}, item ${itemIndex + 1} of ${items.length}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        // A hold pauses the story; it must never open the browser's own menu over the picture.
        onContextMenu={(e) => e.preventDefault()}
        data-testid="story-viewer"
      >
        {staged}
      </div>
    );
  }

  // An introduction on desktop: a 440px panel over a veil, and a tap on the veil closes it.
  if (isIntro) {
    return throughDoor(
      <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,.72)' }} role="dialog" aria-modal="true" onClick={close} data-testid="story-viewer">
        <div className="relative overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ width: 440, height: 'min(880px, calc(100% - 48px))', borderRadius: 36, background: '#000', boxShadow: '0 0 0 1px #262626' }}>
          {staged}
        </div>
      </div>
    );
  }

  return throughDoor(
    <div className="fixed inset-0 z-[100]" style={{ background: '#1A1A1A' }} role="dialog" aria-modal="true" onContextMenu={(e) => e.preventDefault()} data-testid="story-viewer">
      <div className="absolute flex items-center" style={{ left: 32, top: 28, gap: 12, color: '#FFF' }}>
        {/* The handoff drew the old disco-ball-plus-wordmark lockup here; the current mark is
            the drawn wordmark with the ball as its O. Same footprint, right logo. */}
        <PortalLogo cap={24} />
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Close"
        className="pr-viewer-close absolute flex items-center justify-center"
        style={{ right: 32, top: 28, width: 40, height: 40, border: '2px solid rgba(255,255,255,0.4)', color: '#FFF', transition: 'background-color 200ms, color 200ms' }}
      >
        <CloseGlyph size={18} />
      </button>

      <div className="absolute inset-x-0 flex items-start justify-center" style={{ top: 96, gap: 40 }}>
        <Peek reel={prevReel} onClick={() => goPerson(-1)} />
        <Chevron dir="left" onClick={() => goPerson(-1)} disabled={!prevReel} />
        <div className="relative overflow-hidden" style={{ width: 400, height: 711, background: '#000', border: '2px solid #FFF' }}>
          {staged}
        </div>
        <Chevron dir="right" onClick={() => goPerson(1)} disabled={!nextReel} />
        <Peek reel={nextReel} onClick={() => goPerson(1)} />
      </div>

      <p
        className="absolute inset-x-0 text-center font-mono font-bold uppercase"
        style={{ bottom: 24, fontSize: 11, letterSpacing: '0.10em', color: 'rgba(255,255,255,0.45)' }}
      >
        ← → person · space hold · esc close · enter = I&apos;m down
      </p>
    </div>
  );
}

function Peek({ reel, onClick }) {
  if (!reel) return <div style={{ width: 240, height: 427 }} aria-hidden />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative overflow-hidden"
      style={{ width: 240, height: 427, border: '2px solid #2A2A2A', opacity: 0.45, background: '#000' }}
      aria-label={`Portal from ${reel.handle || reel.displayName}`}
    >
      {reel.portal?.media?.thumbnailUrl && (
        <img src={reel.portal.media.thumbnailUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
      <span className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} aria-hidden />
      <span className="absolute inset-0 flex flex-col items-center justify-center" style={{ gap: 10 }}>
        <PortalRing state="seen" size={72} avatarUrl={reel.avatarUrl} initial={(reel.displayName || '?')[0]} />
        <span className="font-mono font-bold text-white" style={{ fontSize: 14 }}>{reel.handle || reel.displayName}</span>
        {reel.expiresAtUtc && (
          <span className="font-mono" style={{ fontSize: 12, color: 'rgba(255,255,255,0.70)' }}>
            {countdown(reel.expiresAtUtc, { style: 'short' }).text}
          </span>
        )}
      </span>
    </button>
  );
}

function Chevron({ dir, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'left' ? 'Previous person' : 'Next person'}
      className="pr-viewer-chevron flex items-center justify-center disabled:opacity-25"
      style={{
        width: 40, height: 40, marginTop: 336,
        background: 'rgba(255,255,255,0.10)',
        border: '2px solid rgba(255,255,255,0.40)',
        color: '#FFF',
        transition: 'background-color 200ms',
      }}
    >
      <svg width="12" height="18" viewBox="0 0 12 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        {dir === 'left' ? <path d="M9 2L3 9l6 7" /> : <path d="M3 2l6 7-6 7" />}
      </svg>
    </button>
  );
}

const g = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'square', strokeLinejoin: 'miter' };
const PlayGlyph = () => <svg width="18" height="18" viewBox="0 0 24 24" {...g}><path d="M6 3l14 9-14 9V3z" /></svg>;
const PauseGlyph = () => <svg width="18" height="18" viewBox="0 0 24 24" {...g}><path d="M8 4v16M16 4v16" /></svg>;
const MutedGlyph = () => <svg width="18" height="18" viewBox="0 0 24 24" {...g}><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M17 9l5 6M22 9l-5 6" /></svg>;
const SoundGlyph = () => <svg width="18" height="18" viewBox="0 0 24 24" {...g}><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M17 8a5 5 0 0 1 0 8" /></svg>;
const DotsGlyph = () => <svg width="18" height="18" viewBox="0 0 24 24" {...g}><path d="M5 12h.01M12 12h.01M19 12h.01" /></svg>;
const CloseGlyph = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M5 5l14 14M19 5L5 19" /></svg>;
